import ContentBlocks from "@/components/ContentBlocks";
import Hero from "@/components/Hero";
import Page from "@/components/Page";
import Seomatic from "@/components/Seomatic";
import useTrack from "@/hooks/useTrack";
import Layout from "@/layouts/Layout";
import craftBatch from "@/lib/craftBatch";
import getCommonServerSideProps from "@/lib/getCommonServerSideProps";
import QueryPage from "@/queries/craft/QueryPage.graphql";
import state from "@/state";
import Head from "next/head";
import React, { useEffect } from "react";

export async function getServerSideProps({
  preview,
  previewData,
  params,
  req,
  res,
  query,
  locale,
}) {
  const commonProps = await getCommonServerSideProps({
    req,
    res,
    query,
    locale,
  });

  const uri = params.slug.join("/");

  const queries = [
    {
      document: QueryPage,
      variables: { uri, site: commonProps.site },
    },
  ];

  const response = await craftBatch(
    queries,
    preview ? previewData.token : null,
  );

  const { pageEntry } = response[0];

  if (!pageEntry) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...commonProps,
      pageEntry,
    },
  };
}

const DynamicPage = ({ site, pageEntry }) => {
  const track = useTrack();

  const heroImage = pageEntry.heroImage?.[0];
  const heroImageMobile = pageEntry.heroImageMobile?.[0] || heroImage;
  const [heroOverlay] = pageEntry?.heroOverlay;

  //	Track event
  track("View Page", {
    type: "Page",
    id: pageEntry.id,
    title: pageEntry.title,
    uri: pageEntry.uri,
  });

  // If there's no hero, set text to dark
  useEffect(() => {
    if (!heroImage) state.header.theme = "dark";
  }, [heroImage]);

  return (
    <Page withHero={!!heroImage}>
      <Seomatic Head={Head} {...pageEntry.seomatic} />
      {site !== "default" && (
        <Head>
          <meta key="robots" name="robots" content="none" />
        </Head>
      )}
      {!!heroImage && (
        <Hero
          key={heroImage.id}
          image={heroImage}
          imageMobile={heroImageMobile}
          heading={heroOverlay?.heading}
          text={heroOverlay?.text}
        />
      )}
      {pageEntry.contentBlocks && (
        <ContentBlocks contentBlocks={pageEntry.contentBlocks} />
      )}
    </Page>
  );
};

const DynamicPageLayout = (page) => {
  const { navigation, site, siteLogo, organisation, hideYcnBranding } =
    page.props;

  return (
    <Layout
      site={site}
      navigation={navigation}
      siteLogo={siteLogo}
      organisation={organisation}
      hideYcnBranding={hideYcnBranding}
    >
      {page}
    </Layout>
  );
};

DynamicPage.getLayout = DynamicPageLayout;

export default DynamicPage;
